import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../storeMain";
import { SaveFormat, saveFormatFromSettings, saveFormatToFlags } from "../../../enums/SaveFormatEnum";
import { SaveMode } from "../../../enums/SaveModeEnum";
import { setSaveFormatOverride } from "../../../../redux/modules/saveFormat";

export interface UseSaveFormatResult {
    // The effective format: the user's per-save pick, or the persisted default.
    saveFormat: SaveFormat;
    setSaveFormat: (format: SaveFormat) => void;
    // Clears the per-save pick back to the persisted default. Call where the old
    // per-save saveMode state used to reset (entering the save step / panel close).
    resetSaveFormat: () => void;
    // Wire-format projections of the effective format. All save plumbing
    // (persistors, pre-commit checks, filename previews) must consume these —
    // never state.user.settings.convertEmailToPDF directly, or a per-save
    // "Keep original formats" pick could not override the persisted default.
    saveMode: SaveMode;
    convertEmailToPDF: boolean;
}

export const useSaveFormat = (): UseSaveFormatResult => {
    const dispatch = useDispatch();
    const persistedDefault = useSelector((state: RootState) =>
        saveFormatFromSettings(state.user?.settings?.convertEmailToPDF));
    // Null-safe: components sharing this hook can render under the standalone
    // stores (advanced search / properties pages) that have no saveFormat slice.
    const override = useSelector((state: RootState) => state.saveFormat?.override ?? null);

    const saveFormat: SaveFormat = override ?? persistedDefault;
    const { saveMode, convertEmailToPDF } = saveFormatToFlags(saveFormat);

    return {
        saveFormat,
        setSaveFormat: (format: SaveFormat) => dispatch(setSaveFormatOverride(format)),
        resetSaveFormat: () => dispatch(setSaveFormatOverride(null)),
        saveMode,
        convertEmailToPDF,
    };
};
